/**
 * Medals — speedrun time tiers (N++ / Mario Maker style), pure + determinism-safe.
 *
 * A run's clear time (in fixed steps) is compared against the level's gold/silver/bronze
 * target SECONDS. Only arithmetic + comparisons here (no transcendentals / RNG / wall-clock),
 * so it is safe to import from `src/engine` and to re-run headlessly in tests / a server resim.
 *
 * Target resolution (see `resolveMedalTimes`):
 *   1. explicit `meta.medalTimes` (authored in the editor), else
 *   2. derived from `meta.timeLimitSec` — a level that sets a clock implies a challenge, so
 *      gold = 0.5×, silver = 0.7×, bronze = 0.9× the limit (beat the clock with margin), else
 *   3. none (a level with no timer + no authored medals awards no medal).
 *
 * PREFER path 1 via `deriveMedalsFromGold`. The timer-fraction default (path 2) is
 * original-to-maker and demonstrably too generous — no studied game derives medals from a
 * death-countdown fraction, and maker's own hand-set curated levels all drifted gold well
 * below 0.5× (docs/research/levels/speedrun-timeattack-medal-research.md §4). The right
 * model is Trackmania's: gold = the AUTHOR's own validated clear time (here: the solver's
 * clear, or a recorded run), with silver/bronze widened off it by archetype. And medals are
 * a MASTERY lever independent of `timeLimitSec` (a FORGIVENESS/pressure lever) — a level can
 * set medals with no death-clock at all (path 1 needs no timer). Path 2 remains a fallback.
 */
import type { LevelMeta, MedalTimes } from './LevelFormat';

export type MedalTier = 'gold' | 'silver' | 'bronze';

/** Fraction of `timeLimitSec` used for each medal when no explicit medalTimes are authored. */
const DERIVED = { gold: 0.5, silver: 0.7, bronze: 0.9 } as const;

/** The effective medal targets for a level, or null when the level awards no medals. */
export function resolveMedalTimes(meta: LevelMeta): MedalTimes | null {
  const m = meta.medalTimes;
  if (m && m.gold > 0 && m.silver > 0 && m.bronze > 0) return m;
  const lim = meta.timeLimitSec;
  if (typeof lim === 'number' && lim > 0) {
    return { gold: lim * DERIVED.gold, silver: lim * DERIVED.silver, bronze: lim * DERIVED.bronze };
  }
  return null;
}

/**
 * Per-archetype (silver, bronze) multipliers of the GOLD anchor. Wider bands where mastery
 * shows as fewer RETRIES rather than raw speed (precision/dashprecision — instant-death loops
 * mean skill = surviving, so more slack) than where it is continuous optimisation
 * (momentum/autorunner — low run-to-run variance, tight bands). Directional starting points —
 * per-level feel is OWED to Isaac's `npm run dev` (research §4).
 */
const GOLD_BANDS: Record<string, { silver: number; bronze: number }> = {
  momentum: { silver: 1.25, bronze: 1.55 },
  autorunner: { silver: 1.25, bronze: 1.55 },
  classic: { silver: 1.30, bronze: 1.70 },
  combat: { silver: 1.35, bronze: 1.75 },
  dashprecision: { silver: 1.40, bronze: 1.90 },
  precision: { silver: 1.45, bronze: 2.00 },
};
const DEFAULT_BAND = { silver: 1.35, bronze: 1.75 };

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Author medal times from a GOLD anchor (seconds) + the level's archetype — the preferred
 * path (see the header). `goldSec` is the author's/solver's clear time; silver & bronze widen
 * off it by the archetype band. gold < silver < bronze holds by construction (both mults > 1),
 * satisfying the LevelFormat ordering invariant. An unknown archetype uses a middle band.
 */
export function deriveMedalsFromGold(goldSec: number, archetypeId?: string): MedalTimes {
  const band = (archetypeId && GOLD_BANDS[archetypeId]) || DEFAULT_BAND;
  return {
    gold: round2(goldSec),
    silver: round2(goldSec * band.silver),
    bronze: round2(goldSec * band.bronze),
  };
}

/** The medal earned for a clear at `frames` fixed steps (60 Hz), or null if slower than bronze. */
export function medalForFrames(frames: number, times: MedalTimes | null): MedalTier | null {
  if (!times) return null;
  const sec = frames / 60;
  if (sec <= times.gold) return 'gold';
  if (sec <= times.silver) return 'silver';
  if (sec <= times.bronze) return 'bronze';
  return null;
}

const GLYPH: Record<MedalTier, string> = { gold: '🥇', silver: '🥈', bronze: '🥉' };

/** Display glyph for a medal tier (🥇/🥈/🥉). */
export function medalGlyph(tier: MedalTier): string { return GLYPH[tier]; }
